import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import MatrixRain from './MatrixRain';
import shineHover from './shineEffect';
import AboutMe from './AboutMe';

const HeroWrapper = styled.div`
  position: relative;
  /* Extra scroll room must exceed the largest scrollY breakpoint used
     below (650px) or the sticky header releases before the About-Me
     reveal finishes animating. 100svh (small viewport height) avoids
     the mobile browser toolbar making 100vh taller than what's
     actually visible on load. */
  height: calc(100vh + 700px);
  height: calc(100svh + 700px);

  /* The pinned reveal below assumes About-Me's content fits in one
     screen. On mobile the card stacks (photo above text) and is far
     taller than that, so the fixed-height/overflow-hidden stage was
     clipping the top of the photo. Drop the pin buffer and let the
     section size to its own content instead. */
  @media (max-width: 900px) {
    height: auto;
  }
`;

const HeaderContainer = styled.header`
  height: 100vh;
  height: 100svh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: sticky;
  top: 0;
  text-align: center;
  background-color: #000000;
  overflow: hidden;

  /* Gradient overlay to focus center and darken edges */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.3) 50%, rgba(0, 0, 0, 0.9) 100%);
    z-index: 2;
    pointer-events: none;
  }

  @media (max-width: 900px) {
    position: relative;
    height: auto;
    min-height: 100svh;
    overflow: visible;
    /* No longer vertically centered (height is now auto, so
       justify-content: center has nothing to center against) — the
       title used to start flush at the very top edge, clipped by and
       overlapping the language toggle button. */
    padding: 96px 24px 40px;
  }
`;

const ContentWrapper = styled(motion.div)`
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  will-change: transform, opacity;

  /* Cancel the scroll-linked fade/slide-out that hides the hero title
     as About-Me is revealed over it — on mobile the two are no longer
     overlaid, so the title should just stay put. */
  @media (max-width: 900px) {
    opacity: 1 !important;
    transform: none !important;
    order: 1;
  }
`;

const RevealLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  /* RevealLayer is markup-order BEFORE ContentWrapper (it needs to sit
     underneath the title on desktop, where it's an absolutely
     positioned overlay and DOM order doesn't affect layout). Once it
     rejoins normal flow on mobile that would render About-Me above the
     hero title, so push it after with flex order instead of reordering
     the markup. */
  @media (max-width: 900px) {
    position: relative;
    inset: auto;
    width: 100%;
    order: 2;
  }
`;

const RevealedAboutMe = styled(motion.div)`
  width: 100%;
  pointer-events: auto;
  will-change: transform, opacity;

  /* Same as ContentWrapper: on mobile About-Me is a normal in-flow
     section, always visible, not a scroll-triggered overlay. */
  @media (max-width: 900px) {
    opacity: 1 !important;
    transform: none !important;
  }
`;

const Title = styled(motion.h1)`
  font-size: clamp(2.5rem, 8vw, 6rem);
  font-weight: 700;
  letter-spacing: -0.04em;
  margin-bottom: 20px;
  ${shineHover('linear-gradient(180deg, #ffffff 0%, #a1a1a6 100%)')}
  line-height: 1.1;

  @media (max-width: 480px) {
    font-size: clamp(2rem, 10vw, 3rem);
  }
`;

const Subtitle = styled(motion.h2)`
  font-size: clamp(1.2rem, 3vw, 1.8rem);
  font-weight: 400;
  color: #86868b;
  max-width: 600px;
  line-height: 1.5;
`;

const GlassButton = styled.button`
  position: absolute;
  top: 30px;
  right: 40px;
  padding: 10px 24px;
  background: rgba(255, 255, 255, 0.05);
  color: #f5f5f7;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  cursor: pointer;
  border-radius: 30px;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-1px);
  }

  @media (max-width: 480px) {
    top: 20px;
    right: 20px;
    padding: 8px 16px;
    font-size: 0.85rem;
  }
`;

const HintBubble = styled(motion.div)`
  position: absolute;
  top: 80px;
  right: 40px;
  background: rgba(255, 255, 255, 0.1);
  color: #f5f5f7;
  padding: 12px 20px;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 400;
  cursor: pointer;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  z-index: 10;

  &::after {
    content: '';
    position: absolute;
    top: -8px;
    right: 32px;
    border-width: 0 8px 8px 8px;
    border-style: solid;
    border-color: transparent transparent rgba(255, 255, 255, 0.1) transparent;
  }

  @media (max-width: 480px) {
    top: 65px;
    right: 20px;
    padding: 10px 15px;
    font-size: 0.8rem;
    
    &::after {
      right: 20px;
    }
  }
`;

const Header = () => {
  const [showHint, setShowHint] = useState(() => !localStorage.getItem('languageHintClosed'));
  const { t, i18n } = useTranslation();
  const { scrollY } = useScroll();

  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  // The matrix rain clears and the title fades, then the About section
  // takes over the same spot as you keep scrolling
  const matrixOpacity = useTransform(scrollY, [0, 450], [0.8, 0.04]);
  const aboutOpacity = useTransform(scrollY, [250, 650], [0, 1]);
  const aboutScale = useTransform(scrollY, [250, 650], [0.92, 1]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
  };

  const closeHint = () => {
    setShowHint(false);
    localStorage.setItem('languageHintClosed', 'true');
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } }
  };

  return (
    <HeroWrapper>
    <HeaderContainer>
      <MatrixRain style={{ opacity: matrixOpacity }} />

      <RevealLayer>
        <RevealedAboutMe style={{ opacity: aboutOpacity, scale: aboutScale }}>
          <AboutMe />
        </RevealedAboutMe>
      </RevealLayer>

      <GlassButton onClick={toggleLanguage}>
        {i18n.language === 'es' ? 'English' : 'Español'}
      </GlassButton>

      {showHint && (
        <HintBubble
          onClick={closeHint}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {t('changeLanguageHint')} ✕
        </HintBubble>
      )}

      <ContentWrapper style={{ y, opacity }} variants={containerVariants} initial="hidden" animate="show">
        <Title variants={itemVariants}>
          {t('title')}
        </Title>
        <Subtitle variants={itemVariants}>
          {t('subtitle')}
        </Subtitle>
      </ContentWrapper>
    </HeaderContainer>
    </HeroWrapper>
  );
};

export default Header;
